import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { push } from 'react-router-redux';
import type { IRestrictionList } from '~/Slices/schema';
import { LoadingStatus } from '~/Slices/schema';
import type { IAppState } from '~/appstate';
import { toastr } from '../helpers/Toastr';
import * as RestrictionListsSlice from './RestrictionListsSlice';

const initialPageState = {
	canSave: false,
	id: -1,
	currentName: '',
	loadingStatus: LoadingStatus.idle,
	hasError: false,
};

export const getRestrictionListName = createAsyncThunk<
	string,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'restrictionListsPage/getRestrictionListName',
	async (listId: number, { dispatch }) => {
		const restrictionList = await dispatch(
			RestrictionListsSlice.getRestrictionList(listId)
		);
		return (restrictionList.payload as IRestrictionList).name;
	}
);

export const editRestrictionListName = createAsyncThunk<
	void,
	RestrictionListsSlice.IEditRestrictionListParameters,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'restrictionListsPage/editRestrictionListName',
	async (
		restrictionList: RestrictionListsSlice.IEditRestrictionListParameters,
		{ dispatch, rejectWithValue }
	) => {
		const result = await dispatch(
			RestrictionListsSlice.editRestrictionList({
				listId: restrictionList.listId,
				newName: restrictionList.newName,
			})
		);
		if (RestrictionListsSlice.editRestrictionList.fulfilled.match(result)) {
			toastr.success({
				title: 'Success!',
				body: 'The Restriction List was renamed.',
			});
			dispatch(push('/restrictions'));
		} else {
			toastr.error({
				title: "We're sorry!",
				body: 'The Restriction List could not be renamed.',
			});
			return rejectWithValue(null);
		}
	}
);

export const restrictionListsPageSlice = createSlice({
	name: 'restrictionListsPage',
	initialState: initialPageState,
	reducers: {
		formChanged(state, action) {
			state.currentName = action.payload;
			state.canSave = validateName(state.currentName);
			state.hasError = !state.canSave;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getRestrictionListName.fulfilled, (state, action) => {
			state.currentName = action.payload;
			state.canSave = false;
			state.hasError = false;
			state.loadingStatus = LoadingStatus.success;
		});
		builder.addCase(getRestrictionListName.pending, (state) => {
			state.loadingStatus = LoadingStatus.loading;
		});
		builder.addCase(getRestrictionListName.rejected, (state) => {
			state.loadingStatus = LoadingStatus.failure;
		});
		builder.addCase(editRestrictionListName.pending, (state) => {
			state.loadingStatus = LoadingStatus.loading;
		});
		builder.addCase(editRestrictionListName.rejected, (state) => {
			state.loadingStatus = LoadingStatus.success;
		});
	},
});

function validateName(input: string) {
	const trimmedInput = input.trim();
	const regex = new RegExp('^.{1,64}$');
	return regex.test(trimmedInput);
}
